/**
 * <slide-show> - wrapper générique autour de Swiper.js.
 *
 * Mobile-first : les valeurs sans préfixe sont la config DESKTOP,
 * activée à partir du breakpoint (768px par défaut). Les valeurs
 * préfixées `mobile-` s'appliquent en dessous.
 *
 * Markup minimal :
 *   <slide-show class="…" data-…>
 *     <div class="swiper">
 *       <div class="swiper-wrapper">
 *         <div class="swiper-slide">…</div>
 *       </div>
 *       <div class="swiper-pagination"></div> (optionnel)
 *     </div>
 *
 *     <!-- flèches : n'importe où dans <slide-show> -->
 *     <button class="slideshow_button_prev">…</button>
 *     <button class="slideshow_button_next">…</button>
 *   </slide-show>
 *
 * data-* (toutes optionnelles) :
 *
 *   Slides par vue :
 *     data-nb-per-slide              (desktop, défaut 1, "auto" accepté)
 *     data-mobile-nb-per-slide       (défaut 1)
 *
 *   Espacement :
 *     data-space-between             (desktop, défaut 24)
 *     data-mobile-space-between      (défaut = data-space-between)
 *
 *   Activations (par couche) :
 *     data-navigation  / data-mobile-navigation     ("true")
 *     data-pagination  / data-mobile-pagination     ("true")
 *     data-loop        / data-mobile-loop           ("true")
 *     data-free-mode   / data-mobile-free-mode      ("true")
 *
 *   Autres :
 *     data-autoplay="true"
 *     data-autoplay-delay            (défaut 4000)
 *     data-breakpoint                (défaut 768)
 *     data-speed                     (défaut 300)
 *     data-direction="horizontal|vertical"
 *     data-mobile-direction          (défaut = data-direction)
 *     data-effect="slide|fade|creative"
 *
 * Au moment où Swiper est instancié, la classe `loaded` est ajoutée
 * sur <slide-show> - utile pour révéler le slider en CSS sans FOUC.
 */

class Slideshow extends HTMLElement {
  constructor() {
    super();
    const d = this.dataset;

    // Slides par vue
    this.nbPerSlide = d.nbPerSlide || 1;
    this.mobileNbPerSlide = d.mobileNbPerSlide || 1;

    // Espacement
    this.spaceBetween = d.spaceBetween || 24;
    this.mobileSpaceBetween = d.mobileSpaceBetween || this.spaceBetween;

    // Activations par couche
    this.navigation = d.navigation === "true";
    this.mobileNavigation = d.mobileNavigation === "true";
    this.pagination = d.pagination === "true";
    this.mobilePagination = d.mobilePagination === "true";
    this.loop = d.loop === "true";
    this.mobileLoop = d.mobileLoop === "true";
    this.freeMode = d.freeMode === "true";
    this.mobileFreeMode = d.mobileFreeMode === "true";

    // Autres
    this.autoplay = d.autoplay === "true";
    this.autoplayDelay = parseInt(d.autoplayDelay) || 4000;
    this.breakpoint = parseInt(d.breakpoint) || 768;
    this.speed = parseInt(d.speed) || 300;
    this.direction = d.direction === "vertical" ? "vertical" : "horizontal";
    this.mobileDirection = d.mobileDirection
      ? d.mobileDirection === "vertical" ? "vertical" : "horizontal"
      : this.direction;
    this.effect = d.effect && d.effect !== "slide" ? d.effect : null;
  }

  connectedCallback() {
    if (this._initialized) return;
    this.#waitForSwiperThen(() => this.initializeSlideshow());
  }

  disconnectedCallback() {
    this._swiper?.destroy(true, true);
    this._initialized = false;
  }

  /** Petite garde : si Swiper n'est pas encore chargé, on re-essaie. */
  #waitForSwiperThen(cb) {
    if (typeof Swiper !== "undefined") return cb();
    let tries = 0;
    const t = setInterval(() => {
      if (typeof Swiper !== "undefined") {
        clearInterval(t);
        cb();
      } else if (++tries > 50) {
        clearInterval(t);
        console.warn("[slide-show] Swiper introuvable après 5s", this);
      }
    }, 100);
  }

  #parseSpv(v) {
    return v === "auto" ? "auto" : parseFloat(v);
  }

  initializeSlideshow() {
    const swiperEl = this.querySelector(".swiper");
    if (!swiperEl) return;
    this._initialized = true;

    // Recherche des éléments de nav/pagination. Si absents, on n'ajoute pas
    // la clé au config - sinon Swiper crashe sur `null.querySelectorAll`.
    const navNext = this.querySelector(".slideshow_button_next");
    const navPrev = this.querySelector(".slideshow_button_prev");
    const pagEl = this.querySelector(".swiper-pagination");

    const desktopBreakpoint = {
      slidesPerView: this.#parseSpv(this.nbPerSlide),
      spaceBetween: parseFloat(this.spaceBetween),
      loop: this.loop,
      freeMode: this.freeMode,
      direction: this.direction,
    };

    const config = {
      // Couche mobile (defaults Swiper)
      slidesPerView: this.#parseSpv(this.mobileNbPerSlide),
      spaceBetween: parseFloat(this.mobileSpaceBetween),
      loop: this.mobileLoop,
      freeMode: this.mobileFreeMode,
      speed: this.speed,
      direction: this.mobileDirection,
      watchOverflow: true,
      grabCursor: true,

      breakpoints: { [this.breakpoint]: desktopBreakpoint },

      on: {
        init: () => {
          this.classList.add("loaded");
          this.dispatchEvent(
            new CustomEvent("slide-show:ready", {
              detail: { swiper: this._swiper },
              bubbles: true,
            }),
          );
        },
      },
    };

    if (navNext || navPrev) {
      config.navigation = {
        nextEl: navNext,
        prevEl: navPrev,
        enabled: this.mobileNavigation,
      };
      desktopBreakpoint.navigation = { enabled: this.navigation };
    }

    if (pagEl) {
      config.pagination = {
        el: pagEl,
        clickable: true,
        enabled: this.mobilePagination,
      };
      desktopBreakpoint.pagination = { enabled: this.pagination };
    }

    // Thumbs : link à un autre <slide-show> par sélecteur CSS. L'autre
    // doit être DÉJÀ initialisé (= venir avant ce slider dans le DOM).
    if (this.dataset.thumbs) {
      const thumbsEl = document.querySelector(this.dataset.thumbs);
      const thumbsInstance = thumbsEl?.swiper;
      if (thumbsInstance) {
        config.thumbs = { swiper: thumbsInstance };
      } else if (thumbsEl) {
        console.warn("[slide-show] thumbs target trouvé mais pas encore initialisé. Place le slide-show des thumbs AVANT celui-ci dans le DOM.", this);
      }
    }

    if (this.effect) config.effect = this.effect;

    if (this.autoplay) {
      config.autoplay = {
        delay: this.autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }

    this._swiper = new Swiper(swiperEl, config);
  }

  /** Accès public à l'instance Swiper (null tant que pas init). */
  get swiper() {
    return this._swiper || null;
  }
}

if (!customElements.get("slide-show")) {
  customElements.define("slide-show", Slideshow);
}
